import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import {
  SCORECARD_HELP, SCORECARD_LINE, SOURCE_KINDS, dateWords, errorMessage, orNull, riskClass, trendClass,
  type RuleKey,
} from "@/features/think-tank/ttModel";

type TtBoard = components["schemas"]["TtBoard"];
type TtRow = components["schemas"]["TtRow"];
type TtEvidenceList = components["schemas"]["TtEvidenceList"];
type TtSource = components["schemas"]["TtSourcePublic"];

const KEY = ["admin", "think-tank"] as const;

/** The scorecard: every next move, one score per rule, the total, the trend and the evidence. */
export function Opportunities() {
  const qc = useQueryClient();
  const [open, setOpen] = useState<{ id: string; rule: RuleKey | null } | null>(null);
  const query = useQuery({
    queryKey: [...KEY, "opportunities"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/opportunities");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: [...KEY] });
  const b = query.data;
  if (query.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (query.isError || !b) {
    return (
      <Card><CardBody>
        <p role="alert" className="text-sm text-red-700">Couldn’t load the scorecard. Your session may have expired.</p>
        <Button size="sm" variant="secondary" className="mt-3" onClick={() => void query.refetch()}>Retry</Button>
      </CardBody></Card>
    );
  }
  return (
    <div className="space-y-4">
      <Card data-testid="tt-scorecard"><CardBody>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-semibold">{SCORECARD_LINE}</h2>
          <span className="text-xs text-zinc-500">
            {b.sample ? <span className="mr-2 rounded bg-zinc-100 px-1.5 py-0.5">Sample scores</span> : null}
            Rules {b.rules_line}
          </span>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500">
                <th className="py-2 pr-2 font-medium">#</th>
                <th className="py-2 pr-2 font-medium">Opportunity</th>
                {b.weights.map(w => (
                  <th key={w.rule} className="px-1 py-2 text-center font-medium" title={`${w.label} · ${w.weight}%`}>
                    {w.short}<span className="block text-[10px] font-normal text-zinc-400">{w.weight}%</span>
                  </th>
                ))}
                <th className="px-1 py-2 text-center font-medium">Score</th>
                <th className="px-1 py-2 text-center font-medium">Trend</th>
                <th className="py-2 pl-2 font-medium">Evidence</th>
              </tr>
            </thead>
            <tbody>
              {b.rows.map(r => <Row key={r.id} r={r} onOpen={rule => setOpen({ id: r.id, rule })} />)}
            </tbody>
          </table>
        </div>
        {!b.rows.length ? <p className="py-3 text-sm text-zinc-500">Nothing on the scorecard yet. Add the first one below.</p> : null}
        <p className="mt-3 text-xs text-zinc-500">{SCORECARD_HELP}</p>
      </CardBody></Card>
      {open ? <Evidence key={`${open.id}:${open.rule}`} id={open.id} rule={open.rule} board={b} onClose={() => setOpen(null)} onChanged={refresh} /> : null}
      <Bench b={b} onOpen={id => setOpen({ id, rule: null })} onChanged={refresh} />
      <AddOpportunity onChanged={refresh} />
    </div>
  );
}

function Row({ r, onOpen }: { r: TtRow; onOpen: (rule: RuleKey | null) => void }) {
  return (
    <tr className="border-b border-zinc-100 align-top" data-testid={`tt-row-${r.id}`}>
      <td className="py-2 pr-2 font-semibold text-zinc-400">{r.rank}</td>
      <td className="py-2 pr-2">
        <button type="button" className="text-left font-medium hover:underline" onClick={() => onOpen(null)}>{r.title}</button>
        {r.instinct ? (
          <span data-testid={`tt-instinct-${r.id}`}
            className={`ml-2 rounded px-1.5 py-0.5 text-xs ${r.instinct.differs ? "bg-amber-100 text-amber-900" : "bg-violet-50 text-violet-800"}`}>
            {r.instinct.label}
          </span>
        ) : null}
        {r.instinct?.line ? <span className="mt-1 block text-xs text-amber-800">{r.instinct.line}</span> : null}
        {!r.scored_all ? <span className="mt-1 block text-xs text-zinc-500">Not every rule is scored yet</span> : null}
      </td>
      {r.cells.map(c => (
        <td key={c.rule} className="px-1 py-2 text-center">
          <button type="button" data-testid={`tt-cell-${r.id}-${c.rule}`} onClick={() => onOpen(c.rule as RuleKey)}
            title={`${c.evidence} evidence item${c.evidence === 1 ? "" : "s"}`}
            className={`min-w-8 rounded px-1.5 py-0.5 hover:bg-zinc-100 ${c.rule === "risk" ? riskClass(c.label) : ""}`}>
            {c.label}
          </button>
        </td>
      ))}
      <td className="px-1 py-2 text-center text-base font-semibold">{r.score}</td>
      <td className={`px-1 py-2 text-center ${trendClass(r.trend_label)}`}>{r.trend_label}</td>
      <td className="py-2 pl-2 text-xs text-zinc-600">
        {r.evidence_line}
        {r.sample ? <span className="ml-1 rounded bg-zinc-100 px-1 py-0.5 text-zinc-500">Sample</span> : null}
      </td>
    </tr>
  );
}

function Bench({ b, onOpen, onChanged }: { b: TtBoard; onOpen: (id: string) => void; onChanged: () => void }) {
  const move = useMutation({
    mutationFn: async (v: { id: string; status: "scored" | "bench" }) => {
      const { error } = await api.PATCH("/api/v1/admin/think-tank/opportunities/{opportunity_id}", {
        params: { path: { opportunity_id: v.id } }, body: { status: v.status },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t move it."));
    },
    onSuccess: onChanged,
  });
  return (
    <Card data-testid="tt-bench"><CardBody>
      <h2 className="font-semibold">Bench · {b.bench.length}</h2>
      <p className="text-sm text-zinc-500">{b.bench_line}</p>
      <ul className="mt-2 divide-y divide-zinc-100">
        {b.bench.map(r => (
          <li key={r.id} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between" data-testid={`tt-bench-${r.id}`}>
            <div className="min-w-0">
              <button type="button" className="text-left text-sm font-medium hover:underline" onClick={() => onOpen(r.id)}>{r.title}</button>
              {r.sample ? <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-500">Sample</span> : null}
              <span className="block text-xs text-zinc-500">Score {r.score} · {r.evidence_line}</span>
            </div>
            <Button size="sm" variant="secondary" disabled={move.isPending} data-testid={`tt-unbench-${r.id}`}
              onClick={() => move.mutate({ id: r.id, status: "scored" })}>Move to the scorecard</Button>
          </li>
        ))}
      </ul>
      {!b.bench.length ? <p className="py-2 text-sm text-zinc-500">The bench is empty.</p> : null}
      {b.rows.length ? (
        <details className="mt-2 text-sm">
          <summary className="cursor-pointer text-zinc-600">Move one to the bench</summary>
          <div className="mt-2 flex flex-wrap gap-2">
            {b.rows.map(r => (
              <Button key={r.id} size="sm" variant="ghost" disabled={move.isPending} data-testid={`tt-to-bench-${r.id}`}
                onClick={() => move.mutate({ id: r.id, status: "bench" })}>{r.title}</Button>
            ))}
          </div>
          <p className="mt-1 text-xs text-zinc-500">The bench keeps its scores and evidence. Nothing is deleted.</p>
        </details>
      ) : null}
      {move.isError ? <p role="alert" className="text-sm text-red-700">{move.error.message}</p> : null}
    </CardBody></Card>
  );
}

function AddOpportunity({ onChanged }: { onChanged: () => void }) {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState<"scored" | "bench">("scored");
  const add = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/api/v1/admin/think-tank/opportunities", {
        body: { title: title.trim(), summary: orNull(summary), status },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t add it."));
    },
    onSuccess: () => { setTitle(""); setSummary(""); onChanged(); },
  });
  return (
    <Card><CardBody>
      <h2 className="font-semibold">Add an opportunity</h2>
      <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <input aria-label="Opportunity" placeholder="What the move is" value={title} onChange={e => setTitle(e.target.value)}
          className="h-9 rounded-md border border-zinc-300 px-2 text-sm" data-testid="tt-add-title" />
        <input aria-label="Why it might matter" placeholder="Why it might matter (optional)" value={summary} onChange={e => setSummary(e.target.value)}
          className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
        <select aria-label="Where it goes" value={status} onChange={e => setStatus(e.target.value as "scored" | "bench")}
          className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
          <option value="scored">On the scorecard</option>
          <option value="bench">On the bench</option>
        </select>
      </div>
      <Button size="sm" className="mt-2" disabled={!title.trim() || add.isPending} data-testid="tt-add" onClick={() => add.mutate()}>Add</Button>
      {add.isError ? <p role="alert" className="mt-2 text-sm text-red-700">{add.error.message}</p> : null}
    </CardBody></Card>
  );
}

/** One score's evidence (or all of an opportunity's), the score itself, and adding evidence. */
function Evidence({ id, rule, board, onClose, onChanged }: {
  id: string; rule: RuleKey | null; board: TtBoard; onClose: () => void; onChanged: () => void;
}) {
  const qc = useQueryClient();
  const row = [...board.rows, ...board.bench].find(r => r.id === id);
  const query = useQuery({
    queryKey: [...KEY, "evidence", id, rule],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/opportunities/{opportunity_id}/evidence", {
        params: { path: { opportunity_id: id }, query: { rule } },
      });
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const sources = useQuery({
    queryKey: [...KEY, "rules"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/rules");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const e: TtEvidenceList | undefined = query.data;
  const changed = () => { void qc.invalidateQueries({ queryKey: [...KEY, "evidence", id] }); onChanged(); };
  return (
    <Card data-testid="tt-evidence"><CardBody>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">{rule && e ? `${e.rule_label} · ${e.title}` : row?.title ?? "Evidence"}</h2>
          <p className="text-sm text-zinc-500">{e?.line ?? "Loading…"}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>
      {rule && e ? <ScoreEditor key={`${e.value}:${e.reason}`} e={e} rule={rule} onChanged={changed} /> : null}
      {!rule && row ? <EditOpportunity key={row.id + row.title} row={row} onChanged={onChanged} /> : null}
      <ul className="mt-3 divide-y divide-zinc-100">
        {e?.items.map(i => (
          <li key={i.id} className="py-2 text-sm" data-testid={`tt-evidence-${i.id}`}>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-600">{i.kind === "pack" ? "Pack" : i.kind === "web" ? "Web" : "Note"}</span>
              {i.url ? <a href={i.url} target="_blank" rel="noreferrer" className="font-medium underline">{i.title}</a> : <span className="font-medium">{i.title}</span>}
              {i.sample ? <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-500">Sample</span> : null}
            </div>
            <div className="text-xs text-zinc-500">
              {dateWords(i.observed_on)}
              {i.people_line ? ` · ${i.people_line}` : ""}
              {i.source_name ? ` · ${i.source_name} (${i.source_trust})` : ""}
              {i.rule ? "" : " · counts for every rule"}
            </div>
            {i.detail ? <p className="mt-1 text-zinc-700">{i.detail}</p> : null}
          </li>
        ))}
      </ul>
      <AddEvidence id={id} rule={rule} sources={sources.data?.sources ?? []} onChanged={changed} />
    </CardBody></Card>
  );
}

function ScoreEditor({ e, rule, onChanged }: { e: TtEvidenceList; rule: RuleKey; onChanged: () => void }) {
  const [value, setValue] = useState(e.value ?? 5);
  const [reason, setReason] = useState(e.reason ?? "");
  const save = useMutation({
    mutationFn: async () => {
      const { error } = await api.PUT("/api/v1/admin/think-tank/opportunities/{opportunity_id}/scores/{rule}", {
        params: { path: { opportunity_id: e.opportunity_id, rule } }, body: { value, reason: orNull(reason) },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t save the score."));
    },
    onSuccess: onChanged,
  });
  return (
    <div className="mt-3 space-y-2 rounded-md border border-zinc-200 p-3" role="group" aria-label="The score">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="tt-score-value" className="text-zinc-600">Score</label>
        <select id="tt-score-value" value={value} onChange={ev => setValue(Number(ev.target.value))}
          className="h-8 rounded-md border border-zinc-300 px-2" data-testid="tt-score-value">
          {Array.from({ length: 11 }, (_, i) => <option key={i} value={i}>{i}{rule === "risk" ? (i >= 6 ? " · low risk" : i >= 4 ? " · med" : " · high risk") : ""}</option>)}
        </select>
        <span className="text-xs text-zinc-500">{rule === "risk" ? "10 is the safest." : "0 to 10."}</span>
      </div>
      <input aria-label="Why this score" placeholder="Why this score (optional)" value={reason} onChange={ev => setReason(ev.target.value)}
        className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm" />
      {e.reason_by ? <p className="text-xs text-zinc-500">{`Last set by ${e.reason_by}`}</p> : null}
      <Button size="sm" disabled={save.isPending} data-testid="tt-score-save" onClick={() => save.mutate()}>Save score</Button>
      {save.isError ? <p role="alert" className="text-sm text-red-700">{save.error.message}</p> : null}
    </div>
  );
}

function EditOpportunity({ row, onChanged }: { row: TtRow; onChanged: () => void }) {
  const [title, setTitle] = useState(row.title);
  const [summary, setSummary] = useState(row.summary ?? "");
  const save = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH("/api/v1/admin/think-tank/opportunities/{opportunity_id}", {
        params: { path: { opportunity_id: row.id } }, body: { title: title.trim(), summary: orNull(summary) },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t save it."));
    },
    onSuccess: onChanged,
  });
  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_2fr_auto]" role="group" aria-label="Edit the opportunity">
      <input aria-label="Opportunity" value={title} onChange={ev => setTitle(ev.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
      <input aria-label="Why it might matter" placeholder="Why it might matter (optional)" value={summary} onChange={ev => setSummary(ev.target.value)}
        className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
      <Button size="sm" variant="secondary" disabled={!title.trim() || save.isPending} data-testid="tt-edit-save" onClick={() => save.mutate()}>Save</Button>
      {save.isError ? <p role="alert" className="text-sm text-red-700 sm:col-span-3">{save.error.message}</p> : null}
    </div>
  );
}

function AddEvidence({ id, rule, sources, onChanged }: { id: string; rule: RuleKey | null; sources: TtSource[]; onChanged: () => void }) {
  const [kind, setKind] = useState<"web" | "note">("web");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [source, setSource] = useState("");
  const [detail, setDetail] = useState("");
  const add = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/api/v1/admin/think-tank/opportunities/{opportunity_id}/evidence", {
        params: { path: { opportunity_id: id } },
        body: { rule, kind, title: title.trim(), url: orNull(url), source_id: source || null, detail: orNull(detail) },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t add that."));
    },
    onSuccess: () => { setTitle(""); setUrl(""); setDetail(""); onChanged(); },
  });
  return (
    <div className="mt-3 space-y-2 border-t border-zinc-100 pt-3" role="group" aria-label="Add evidence">
      <h3 className="text-sm font-medium">Add evidence{rule ? " for this score" : " for every rule"}</h3>
      <div className="grid gap-2 sm:grid-cols-2">
        <select aria-label="Kind" value={kind} onChange={e => setKind(e.target.value as "web" | "note")} className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
          <option value="web">From the web</option>
          <option value="note">Our own note</option>
        </select>
        <select aria-label="Source" value={source} onChange={e => setSource(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
          <option value="">Source (optional)</option>
          {sources.map(s => <option key={s.id} value={s.id}>{s.name} · {s.trust}</option>)}
        </select>
        <input aria-label="What it shows" placeholder="What it shows" value={title} onChange={e => setTitle(e.target.value)}
          className="h-9 rounded-md border border-zinc-300 px-2 text-sm" data-testid="tt-evidence-title" />
        <input aria-label="Link" placeholder="Link (https://…)" value={url} onChange={e => setUrl(e.target.value)}
          className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
      </div>
      <textarea aria-label="Details" placeholder="Details or a short quote (optional)" value={detail} onChange={e => setDetail(e.target.value)}
        className="min-h-16 w-full rounded-md border border-zinc-300 p-2 text-sm" />
      <Button size="sm" disabled={!title.trim() || add.isPending} data-testid="tt-evidence-add" onClick={() => add.mutate()}>Add evidence</Button>
      {add.isError ? <p role="alert" className="text-sm text-red-700">{add.error.message}</p> : null}
      <p className="text-xs text-zinc-500">
        Kinds of source: {SOURCE_KINDS.map(k => k[1]).join(", ")}. AI web research runs when keys are connected; until then, evidence is added by hand.
      </p>
    </div>
  );
}
